
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { CalendarDays, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BenchmarkSidebarProps {
  filters: {
    dateFrom: string;
    dateTo: string;
    product: string;
    locationGroups: string[];
    frequency: 'daily' | 'weekly' | 'monthly';
    comparisonType: 'min' | 'avg';
  };
  onFiltersChange: (filters: any) => void;
  products: string[];
}

const LOCATION_GROUPS = [
  { id: 'local-shops', label: 'Local Shops', default: true },
  { id: 'sunday-market', label: 'Sunday Market', default: true },
  { id: 'supermarket', label: 'Supermarket', default: true },
  { id: 'ecommerce', label: 'Ecommerce', default: true },
  { id: 'distribution-center', label: 'Distribution Center', default: true },
  { id: 'farm-prices', label: 'Farm Prices', default: true },
  { id: 'chipchip-sales-db', label: 'ChipChip Sales (Database)', default: true },
  { id: 'chipchip-sales', label: 'ChipChip Sales (API)', default: false },
];

const BenchmarkSidebar: React.FC<BenchmarkSidebarProps> = ({
  filters,
  onFiltersChange,
  products,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleLocationGroupChange = (groupId: string, checked: boolean) => {
    const newGroups = checked
      ? [...filters.locationGroups, groupId]
      : filters.locationGroups.filter(id => id !== groupId);
    
    onFiltersChange({ ...filters, locationGroups: newGroups });
  };

  return (
    <div className="w-56 bg-gray-50 border-r p-1 space-y-2 overflow-y-auto">
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-900 px-1">Price Benchmark Filters</h2>
        
        {/* Date Range */}
        <Card className="border-gray-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              Date Range
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 p-2 pt-1">
            <div>
              <Label htmlFor="dateFrom" className="text-xs">From</Label>
              <Input
                id="dateFrom"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => onFiltersChange({ ...filters, dateFrom: e.target.value })}
                className="h-6 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="dateTo" className="text-xs">To</Label>
              <Input
                id="dateTo"
                type="date"
                value={filters.dateTo}
                onChange={(e) => onFiltersChange({ ...filters, dateTo: e.target.value })}
                className="h-6 text-xs"
              />
            </div>
          </CardContent>
        </Card>

        {/* Product Selection */}
        <Card className="border-gray-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs">Product</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-1">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between h-6 text-xs px-2"
                >
                  {filters.product
                    ? products.find((product) => product === filters.product)
                    : "Select Product..."}
                  <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search products..." className="h-8 text-xs" />
                  <CommandList>
                    <CommandEmpty>No product found.</CommandEmpty>
                    <CommandGroup>
                      {products.map((product) => (
                        <CommandItem
                          key={product}
                          value={product}
                          onSelect={(currentValue) => {
                            onFiltersChange({ ...filters, product: currentValue === filters.product ? "" : currentValue });
                            setOpen(false);
                          }}
                          className="text-xs"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-3 w-3",
                              filters.product === product ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {product}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {/* Location Groups */}
        <Card className="border-gray-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs">Location Groups for Comparison</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 p-2 pt-1">
            {LOCATION_GROUPS.map((group) => (
              <div key={group.id} className="flex items-center space-x-2">
                <Checkbox
                  id={group.id}
                  checked={filters.locationGroups.includes(group.id)}
                  onCheckedChange={(checked) => handleLocationGroupChange(group.id, checked as boolean)}
                  className="h-3 w-3"
                />
                <Label htmlFor={group.id} className="text-xs font-normal">
                  {group.label}
                </Label>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Data Frequency */}
        <Card className="border-gray-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs">Data Frequency</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-1">
            <Select value={filters.frequency} onValueChange={(value: 'daily' | 'weekly' | 'monthly') => onFiltersChange({ ...filters, frequency: value })}>
              <SelectTrigger className="h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily" className="text-xs">Daily</SelectItem>
                <SelectItem value="weekly" className="text-xs">Weekly</SelectItem>
                <SelectItem value="monthly" className="text-xs">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Comparison Type */}
        <Card className="border-gray-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs">Comparison Type</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-1">
            <RadioGroup 
              value={filters.comparisonType} 
              onValueChange={(value: 'min' | 'avg') => onFiltersChange({ ...filters, comparisonType: value })}
              className="space-y-1"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="min" id="min" className="h-3 w-3" />
                <Label htmlFor="min" className="text-xs">Minimum</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="avg" id="avg" className="h-3 w-3" />
                <Label htmlFor="avg" className="text-xs">Average</Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BenchmarkSidebar;
